<template>
    <div class="admin-dashboard">
        <!-- Quick Stats Section -->
        <div class="stats-overview">
            <el-row :gutter="20">
                <el-col :span="6" v-for="stat in stats" :key="stat.title">
                    <el-card shadow="hover" class="stat-card">
                        <template #default>
                            <div class="stat-content">
                                <div class="stat-icon" :style="{ backgroundColor: stat.color }">
                                    <el-icon>
                                        <component :is="stat.icon" />
                                    </el-icon>
                                </div>
                                <div class="stat-info">
                                    <div class="stat-title">{{ stat.title }}</div>
                                    <div class="stat-value">{{ stat.value }}</div>
                                </div>
                            </div>
                        </template>
                    </el-card>
                </el-col>
            </el-row>
        </div>

        <!-- User Management Section -->
        <el-card class="content-card" shadow="never">
            <template #header>
                <div class="card-header">
                    <div class="header-title">
                        <el-icon>
                            <User />
                        </el-icon>
                        <span>用户管理</span>
                    </div>
                    <el-button type="primary" size="default" @click="showAddUserDialog" class="add-btn">
                        <el-icon>
                            <Plus />
                        </el-icon> 新增用户
                    </el-button>
                </div>
            </template>

            <el-table :data="users" style="width: 100%" v-loading="loading" class="custom-table">
                <el-table-column prop="id" label="ID" width="80" align="center" />
                <el-table-column prop="username" label="用户名" width="180">
                    <template #default="scope">
                        <div class="user-info-cell">
                            <el-avatar :size="32" :src="scope.row.avatar || defaultAvatar" />
                            <span>{{ scope.row.username }}</span>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="role" label="角色" width="100">
                    <template #default="scope">
                        <el-tag :type="getRoleType(scope.row.role)" effect="light">
                            {{ getRoleLabel(scope.row.role) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="status" label="状态" width="100">
                    <template #default="scope">
                        <el-tag :type="getStatusType(scope.row.status)" effect="dark">
                            {{ getStatusLabel(scope.row.status) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column prop="totalPoints" label="积分余额" width="110" align="center">
                    <template #default="scope">
                        <span class="points-balance">{{ scope.row.totalPoints ?? 0 }}</span>
                    </template>
                </el-table-column>
                <el-table-column prop="inviterName" label="邀请人" width="120" align="center">
                    <template #default="scope">
                        <span v-if="scope.row.inviterName" class="inviter-tag">{{ scope.row.inviterName }}</span>
                        <span v-else style="color: #c0c4cc">-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="school" label="学校" min-width="200" show-overflow-tooltip />
                <el-table-column prop="subject" label="学科" width="120" align="center" />
                <el-table-column prop="grade" label="年级" width="120" align="center" />
                <el-table-column label="操作" width="220" fixed="right" align="center">
                    <template #default="scope">
                        <el-button link type="warning" @click="openFinance(scope.row)">流水</el-button>
                        <el-button link type="primary" @click="editUser(scope.row)">编辑</el-button>
                        <el-button link type="danger" @click="deleteUser(scope.row)">删除</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- Unified Edit User Dialog -->
        <el-dialog v-model="editDialogVisible" title="用户信息编辑" width="480px" class="custom-dialog">
            <el-form :model="editForm" label-position="top">
                <el-row :gutter="20">
                    <el-col :span="24">
                        <el-form-item label="用户名">
                            <el-input v-model="editForm.username" disabled />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="管理角色">
                            <el-select v-model="editForm.role" style="width: 100%" placeholder="选择角色">
                                <el-option label="学生" value="student" />
                                <el-option label="教师" value="teacher" />
                                <el-option label="管理员" value="admin" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="账户状态">
                            <el-select v-model="editForm.status" style="width: 100%" placeholder="选择状态">
                                <el-option label="正常" value="active" />
                                <el-option label="禁言" value="muted" />
                                <el-option label="封禁" value="banned" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="24">
                        <el-form-item label="学校名称">
                            <el-input v-model="editForm.school" placeholder="输入所在学校" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="授课学科">
                            <el-input v-model="editForm.subject" placeholder="授课学科" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="授课年级">
                            <el-input v-model="editForm.grade" placeholder="授课年级" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="editDialogVisible = false">取消</el-button>
                    <el-button type="primary" @click="saveUser" :loading="saving">确认保存</el-button>
                </span>
            </template>
        </el-dialog>

        <!-- User Finance (Points / Payment) Dialog -->
        <el-dialog v-model="financeDialogVisible" title="用户流水（积分 / 资金）" width="920px" class="custom-dialog">
            <div class="finance-summary" v-if="financeUser">
                <div class="finance-user">
                    {{ financeUser.username }}
                    <span class="finance-uid">ID: {{ financeUser.id }}</span>
                </div>
                <div class="finance-stats">
                    <div class="finance-stat">
                        <div class="label">当前积分</div>
                        <div class="value points">{{ financeSummary.total_points || 0 }}</div>
                    </div>
                    <div class="finance-stat">
                        <div class="label">累计获得</div>
                        <div class="value">+{{ financeSummary.total_earned || 0 }}</div>
                    </div>
                    <div class="finance-stat">
                        <div class="label">累计消耗</div>
                        <div class="value minus">{{ financeSummary.total_spent || 0 }}</div>
                    </div>
                    <div class="finance-stat">
                        <div class="label">累计支付(元)</div>
                        <div class="value">¥ {{ (financeSummary.total_paid || 0).toFixed(2) }}</div>
                    </div>
                    <div class="finance-stat">
                        <div class="label">累计退款(元)</div>
                        <div class="value">¥ {{ (financeSummary.total_refunded || 0).toFixed(2) }}</div>
                    </div>
                </div>
            </div>

            <el-tabs v-model="financeTab">
                <el-tab-pane label="积分流水" name="points">
                    <el-table :data="pointsRecords" v-loading="financeLoading" size="small" height="340" stripe>
                        <el-table-column label="时间" width="160">
                            <template #default="scope">{{ formatTime(scope.row.CreatedAt) }}</template>
                        </el-table-column>
                        <el-table-column label="类型" width="110">
                            <template #default="scope">{{ pointTypeLabel(scope.row.type) }}</template>
                        </el-table-column>
                        <el-table-column label="变动" width="90" align="right">
                            <template #default="scope">
                                <span :class="scope.row.delta >= 0 ? 'delta-plus' : 'delta-minus'">
                                    {{ scope.row.delta > 0 ? '+' : '' }}{{ scope.row.delta }}
                                </span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="balance_after" label="变动后余额" width="110" align="right" />
                        <el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip />
                    </el-table>
                    <el-pagination class="finance-pager" layout="total, prev, pager, next" :total="pointsTotal"
                        :page-size="pointsSize" :current-page="pointsPage" @current-change="handlePointsPageChange" />
                </el-tab-pane>
                <el-tab-pane label="资金流水" name="orders">
                    <el-table :data="orders" v-loading="financeLoading" size="small" height="340" stripe>
                        <el-table-column label="时间" width="160">
                            <template #default="scope">{{ formatTime(scope.row.CreatedAt) }}</template>
                        </el-table-column>
                        <el-table-column prop="out_trade_no" label="商户单号" width="170" show-overflow-tooltip />
                        <el-table-column prop="body" label="商品" min-width="130" show-overflow-tooltip />
                        <el-table-column label="应付(元)" width="90" align="right">
                            <template #default="scope">{{ scope.row.total_fee || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="实付(元)" width="90" align="right">
                            <template #default="scope">{{ scope.row.money || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="状态" width="90" align="center">
                            <template #default="scope">
                                <el-tag :type="scope.row.finished ? 'success' : 'info'" size="small" effect="light">
                                    {{ scope.row.finished ? '已支付' : '待支付' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="退款" width="120" align="center">
                            <template #default="scope">
                                <el-tag v-if="scope.row.refund_status > 0"
                                    :type="scope.row.refund_status === 2 ? 'success' : 'warning'" size="small" effect="light">
                                    {{ scope.row.refund_status === 2 ? '已退款' : '退款中' }}
                                    <span v-if="scope.row.refund_amount"> ¥{{ scope.row.refund_amount }}</span>
                                </el-tag>
                                <span v-else class="empty-dash">-</span>
                            </template>
                        </el-table-column>
                    </el-table>
                    <el-pagination class="finance-pager" layout="total, prev, pager, next" :total="ordersTotal"
                        :page-size="ordersSize" :current-page="ordersPage" @current-change="handleOrdersPageChange" />
                </el-tab-pane>
            </el-tabs>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="financeDialogVisible = false">关闭</el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { User, Plus, ChatDotSquare, Document, Files, Connection } from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import api from '@/utils/api'

const users = ref([])
const loading = ref(false)
const saving = ref(false)
const editDialogVisible = ref(false)
const defaultAvatar = "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png"

const statsData = ref({
    userCount: 0,
    questionCount: 0,
    paperCount: 0,
    newUsersToday: 0
})

const stats = computed(() => [
    { title: '总用户数', value: statsData.value.userCount, icon: 'User', color: '#eef2ff', iconColor: '#4f46e5' },
    { title: '今日新增', value: statsData.value.newUsersToday, icon: 'Plus', color: '#ecfdf5', iconColor: '#10b981' },
    { title: '题目总量', value: statsData.value.questionCount, icon: 'Document', color: '#fef3c7', iconColor: '#f59e0b' },
    { title: '试卷总量', value: statsData.value.paperCount, icon: 'Files', color: '#fff1f2', iconColor: '#f43f5e' }
])

const editForm = ref({
    id: null,
    username: '',
    role: '',
    status: 'active',
    school: '',
    subject: '',
    grade: ''
})

const getStatusType = (status) => {
    const types = { active: 'success', muted: 'warning', banned: 'danger' }
    return types[status] || 'info'
}

const getStatusLabel = (status) => {
    const labels = { active: '正常', muted: '禁言', banned: '封禁' }
    return labels[status] || status
}

const getRoleType = (role) => {
    const types = { admin: 'danger', teacher: 'primary', student: 'info' }
    return types[role] || 'info'
}

const getRoleLabel = (role) => {
    const labels = { admin: '管理员', teacher: '教师', student: '学生' }
    return labels[role] || role
}

const loadUsers = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/users')
        users.value = res.data.users || []
    } catch (error) {
        ElMessage.error('加载用户失败')
    } finally {
        loading.value = false
    }
}

const loadStats = async () => {
    try {
        const res = await api.get('/api/admin/stats')
        statsData.value = res.data
    } catch (e) {
        console.error('Failed to load stats', e)
    }
}

const editUser = (row) => {
    editForm.value = { ...row }
    editDialogVisible.value = true
}

const saveUser = async () => {
    saving.value = true
    try {
        await api.put(`/api/admin/users/${editForm.value.id}`, {
            role: editForm.value.role,
            status: editForm.value.status,
            school: editForm.value.school,
            subject: editForm.value.subject,
            grade: editForm.value.grade
        })
        ElMessage.success('更新成功')
        editDialogVisible.value = false
        await loadUsers()
    } catch (error) {
        ElMessage.error('更新失败')
    } finally {
        saving.value = false
    }
}

const deleteUser = (row) => {
    ElMessageBox.confirm(`确定彻底删除用户 ${row.username} 吗？此操作不可撤销。`, '安全警示', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async () => {
        try {
            await api.delete(`/api/admin/users/${row.id}`)
            ElMessage.success('删除成功')
            await loadUsers()
        } catch (error) {
            ElMessage.error('删除失败')
        }
    }).catch(() => { })
}

const showAddUserDialog = () => {
    ElMessage.info('快捷新增功能开发中...')
}

// ── 用户流水（积分 / 资金） ──
const financeDialogVisible = ref(false)
const financeLoading = ref(false)
const financeUser = ref(null)
const financeTab = ref('points')
const financeSummary = ref({})
const pointsRecords = ref([])
const pointsTotal = ref(0)
const pointsPage = ref(1)
const pointsSize = ref(20)
const orders = ref([])
const ordersTotal = ref(0)
const ordersPage = ref(1)
const ordersSize = ref(20)

const POINT_TYPE_LABELS = {
    daily_checkin: '每日签到',
    checkin_milestone: '连签奖励',
    post: '发帖奖励',
    comment: '评论奖励',
    like: '获赞奖励',
    invite_register: '邀请注册',
    invite_pay: '邀请付费',
    recharge: '积分充值',
    download: '下载消耗',
    expire: '积分过期',
    activity: '活动奖励',
    register_gift: '注册礼包',
    feedback: '反馈奖励',
    system: '管理员调整'
}

const pointTypeLabel = (t) => POINT_TYPE_LABELS[t] || t || '-'

const formatTime = (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-')

const loadFinance = async () => {
    if (!financeUser.value) return
    financeLoading.value = true
    try {
        const res = await api.get(`/api/admin/users/${financeUser.value.id}/finance`, {
            params: {
                pointsPage: pointsPage.value,
                pointsSize: pointsSize.value,
                ordersPage: ordersPage.value,
                ordersSize: ordersSize.value
            }
        })
        financeSummary.value = res.data.summary || {}
        pointsRecords.value = res.data.points_records || []
        pointsTotal.value = res.data.points_total || 0
        orders.value = res.data.orders || []
        ordersTotal.value = res.data.orders_total || 0
    } catch (error) {
        ElMessage.error('加载用户流水失败')
    } finally {
        financeLoading.value = false
    }
}

const openFinance = (row) => {
    financeUser.value = row
    financeTab.value = 'points'
    pointsPage.value = 1
    ordersPage.value = 1
    financeDialogVisible.value = true
    loadFinance()
}

const handlePointsPageChange = (p) => {
    pointsPage.value = p
    loadFinance()
}

const handleOrdersPageChange = (p) => {
    ordersPage.value = p
    loadFinance()
}

onMounted(() => {
    loadUsers()
    loadStats()
})
</script>

<style scoped>
.admin-dashboard {
    animation: fadeIn 0.5s ease-out;
}

.stats-overview {
    margin-bottom: 24px;
}

.stat-card {
    border: none;
    border-radius: 12px;
    transition: transform 0.3s;
}

.stat-card:hover {
    transform: translateY(-4px);
}

.stat-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
}

.stat-title {
    font-size: 14px;
    color: #909399;
    margin-bottom: 4px;
}

.stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #303133;
}

.content-card {
    border: none;
    border-radius: 12px;
    background: #fff;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
}

.custom-table {
    border-radius: 8px;
    overflow: hidden;
}

.user-info-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.add-btn {
    border-radius: 8px;
}

.inviter-tag {
    background-color: #f0f9eb;
    color: #67c23a;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 13px;
    border: 1px solid #e1f3d8;
}

/* Transitions */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

:deep(.el-card__header) {
    padding: 18px 20px;
    border-bottom: 1px solid #f2f6fc;
}

:deep(.el-table th.el-table__cell) {
    background-color: #fafafa;
    color: #606266;
    font-weight: 600;
}
.points-balance {
    font-weight: 700;
    color: #e6a23c;
}

/* Finance dialog */
.finance-summary {
    margin-bottom: 12px;
}

.finance-user {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
}

.finance-uid {
    margin-left: 8px;
    font-size: 12px;
    font-weight: 400;
    color: #909399;
}

.finance-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.finance-stat {
    flex: 1;
    min-width: 130px;
    background: #fafbfd;
    border: 1px solid #f0f2f5;
    border-radius: 8px;
    padding: 10px 14px;
}

.finance-stat .label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 6px;
}

.finance-stat .value {
    font-size: 18px;
    font-weight: 700;
    color: #303133;
}

.finance-stat .value.points {
    color: #e6a23c;
}

.finance-stat .value.minus {
    color: #f56c6c;
}

.finance-pager {
    margin-top: 12px;
    justify-content: flex-end;
}

.delta-plus {
    color: #67c23a;
    font-weight: 600;
}

.delta-minus {
    color: #f56c6c;
    font-weight: 600;
}

.empty-dash {
    color: #c0c4cc;
}
</style>
